import {
  type FocusEvent,
  type KeyboardEvent,
  useEffect,
  useRef,
  useState,
} from "react";

import { useTranslation } from "next-i18next";

import { analyticsService } from "@/common/services/analytics/analytics.service";
import { cn } from "@/common/utils/css.utils";
import type { RangeFacet as RangeFacetData } from "@/features/search/api/types";
import {
  useSearchContext,
  useSearchNavigationContext,
} from "@/features/search/context/SearchContext";
import {
  type RangeBounds,
  normalizeRange,
  rangesEqual,
} from "@/features/search/utils/range.utils";

type RangeFacetProps = {
  facet: RangeFacetData;
};

const toInput = (value: number | null | undefined) =>
  value == null ? "" : String(Math.round(value));

const toDraft = (bounds: RangeBounds) => ({
  min: toInput(bounds.min),
  max: toInput(bounds.max),
});

const INPUT_CLASS =
  "w-full rounded-md border border-grey-200 px-3 py-2 font-body text-sm focus:border-brand-500 focus:outline-none";

/* Room for the mark, on whichever side it sits. */
const UNIT_PADDING = { prefix: "pl-7", suffix: "pr-9" } as const;
const UNIT_POSITION = { prefix: "left-3", suffix: "right-3" } as const;

/**
 * Numeric range facet (price, dimensions, ...). The inputs hold a draft while
 * typing and commit on blur / Enter; `stats` supply the placeholders.
 */
export const RangeFacet = ({ facet }: RangeFacetProps) => {
  const { t } = useTranslation();
  const { setRange } = useSearchNavigationContext();

  const applied: RangeBounds = {
    min: facet.selected?.min ?? null,
    max: facet.selected?.max ?? null,
  };

  const [draft, setDraft] = useState(() => toDraft(applied));
  const [lastApplied, setLastApplied] = useState(applied);

  // Re-seed the inputs when the applied range changes elsewhere (chips, reset,
  // new results). Adjusting state during render is React's recommended
  // alternative to a syncing effect — it re-renders before paint, so there is
  // no flash of stale values.
  if (!rangesEqual(applied, lastApplied)) {
    setLastApplied(applied);
    setDraft(toDraft(applied));
  }

  /*
   * Where the browser was sending focus when a commit interrupted it.
   *
   * Committing disables the whole panel while the new results load, and a control that goes
   * disabled loses focus — so tabbing out of "min" left the caret nowhere instead of in "max".
   * Remembering the blur's `relatedTarget` and restoring it once the panel wakes up keeps the
   * keyboard path intact: type a minimum, tab, type a maximum.
   */
  const nextFocus = useRef<HTMLElement | null>(null);
  /* What was last sent — `applied` only catches up when the response lands. */
  const inFlight = useRef<RangeBounds | null>(null);
  const { query } = useSearchContext();
  const isLoading = query.isPlaceholderData;

  useEffect(() => {
    if (isLoading) {
      return;
    }

    inFlight.current = null;

    const target = nextFocus.current;
    nextFocus.current = null;

    /* A facet can disappear between the two renders (its section collapsed, the response dropped
     * it), and focusing a detached node silently moves focus to the body. */
    if (target && document.contains(target)) {
      target.focus();
    }
  }, [isLoading]);

  const commit = (movingTo: EventTarget | null = null) => {
    const next = normalizeRange(draft.min, draft.max);

    /* Skip a no-op navigation so merely tabbing through doesn't refetch — including the second
     * blur the panel causes itself: disabling the fields blurs whichever one holds focus, which
     * would otherwise commit the very same range again. */
    if (
      rangesEqual(next, applied) ||
      (inFlight.current !== null && rangesEqual(next, inFlight.current))
    ) {
      return;
    }

    inFlight.current = next;

    /* Only when there is somewhere to go: a blur with no `relatedTarget` — Enter, a click on empty
     * space — must not wipe the target an earlier commit is still waiting to restore. */
    if (movingTo instanceof HTMLElement) {
      nextFocus.current = movingTo;
    }

    setRange(facet.key, next.min, next.max);

    /* Only on commit, never per keystroke — a draft isn't a filter until it's applied. GA4 only
     * takes strings and numbers, so the range goes out as `100-500` / `100-` / `-500`, the format
     * the apps send (WHO-5834). A cleared range sends nothing. */
    if (next.min != null || next.max != null) {
      analyticsService.trackFilterClickList(
        facet.key,
        `${next.min ?? ""}-${next.max ?? ""}`
      );
    }
  };

  const handleBlur = (event: FocusEvent<HTMLInputElement>) =>
    commit(event.relatedTarget);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      event.currentTarget.blur();
    }
  };

  /* `unit` is the service's call — "€" before the number, "cm" after it, null for a plain count. */
  const { unit } = facet;

  const bound = (bound: "min" | "max", label: string) => (
    /* `numeric-input` drops the spinner arrows, which would otherwise sit under a "cm" suffix. */
    <div className="numeric-input relative flex-1">
      {unit && (
        <span
          aria-hidden
          className={cn(
            "pointer-events-none absolute inset-y-0 flex items-center font-body text-sm text-grey-500",
            UNIT_POSITION[unit.position]
          )}
        >
          {unit.symbol}
        </span>
      )}
      <input
        type="number"
        inputMode="numeric"
        aria-label={label}
        /* "min" / "max", not the range's own bounds: which box is which is the thing a shopper
         * cannot guess (WHO-5190), while the extremes are outlier listings that read as noise. */
        placeholder={label}
        value={draft[bound]}
        onChange={event =>
          setDraft(current => ({ ...current, [bound]: event.target.value }))
        }
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={cn(INPUT_CLASS, unit && UNIT_PADDING[unit.position])}
      />
    </div>
  );

  return (
    <div className="flex items-center space-x-2">
      {bound("min", t("listing:filter-min-placeholder"))}
      <span className="text-grey-400">–</span>
      {bound("max", t("listing:filter-max-placeholder"))}
    </div>
  );
};
